"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { AssetImage } from "@/components/optilase/asset-image";
import { CtaButton } from "@/components/optilase/cta-button";
import { headingOn, onSurface, surfaceFrom } from "@/components/optilase/surface";
import { cn } from "@/lib/utils";

type Asset = { filename?: string; alt?: string; focus?: string };

type LpIntroBlok = SbBlokData & {
  heading?: string;
  body?: string;
  cta_label?: string;
  image?: Asset;
  bg_color?: unknown;
};

export default function LpIntro({ blok }: { blok: LpIntroBlok }) {
  const paragraphs = (blok.body ?? "")
    .split(/\n{2,}|\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (!blok.heading && paragraphs.length === 0) return null;

  const [lead, ...rest] = paragraphs;
  const image = blok.image?.filename ? blok.image : null;
  const surface = surfaceFrom(blok.bg_color);

  return (
    <section
      {...storyblokEditable(blok)}
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-12 md:px-10 lg:px-20 lg:py-20", surface ? "" : "bg-teal-01")}
    >
      <div className="mx-auto w-full max-w-7xl">
        <div
          className={cn(
            "flex flex-col gap-10 lg:flex-row lg:gap-16",
            image ? "lg:items-center" : "lg:items-start",
          )}
        >
          {image && (
            <div className="relative mx-auto aspect-square w-64 shrink-0 overflow-hidden rounded-full sm:w-80 lg:mx-0 lg:w-105">
              <AssetImage
                src={image.filename as string}
                focus={image.focus}
                alt={image.alt || ""}
                fill
                sizes="(min-width: 1115px) 420px, 320px"
                className="object-cover"
              />
            </div>
          )}

          <div
            className={cn("flex flex-col gap-5", image ? "lg:min-w-0 lg:flex-1" : "lg:max-w-215")}
          >
            {blok.heading && (
              <h2 className={cn("text-section", headingOn(surface, "text-teal-08"))}>
                {blok.heading}
              </h2>
            )}

            {lead && (
              <p
                className={cn(
                  "text-xl leading-relaxed font-semibold lg:text-2xl",
                  onSurface(surface, "text-white", "text-grey-09", "text-grey-09"),
                )}
              >
                {lead}
              </p>
            )}

            {rest.map((p, i) => (
              <p
                key={i}
                className={cn(
                  "text-base leading-relaxed lg:text-lg",
                  onSurface(surface, "text-white/80", "text-grey-07", "text-grey-07"),
                )}
              >
                {p}
              </p>
            ))}

            {blok.cta_label && (
              <CtaButton
                action="open_booking"
                variant="orange"
                keepOnMobile
                className="mt-3 w-full sm:w-fit"
              >
                {blok.cta_label}
              </CtaButton>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
